import { useState, useMemo, type ReactNode } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/16/solid';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Bars3Icon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { getAllColumns } from '../../../../../../components/Table/columns/index';
import { ColumnDefinition } from '../../../../../../components/Table/columns/shared/column-types';
import SharedButton from '@/global/Button/Button';

function getHeaderText(header: unknown): string {
  if (header == null) return '';
  if (typeof header === 'string') return header.trim();
  if (typeof header === 'number') return String(header);
  if (Array.isArray(header)) return header.map(getHeaderText).join('').trim();
  if (typeof header === 'object' && header !== null && 'props' in header) {
    const el = header as { props?: { children?: ReactNode } };
    if (el.props?.children != null) return getHeaderText(el.props.children);
  }
  return '';
}

const COLUMN_LABEL_FALLBACK: Record<string, string> = {
  score: 'Score',
  sourceScore: 'My Score',
};

export interface SidebarColumnsProps {
  view: string;
  tab: string;
  source?: string;
  isEvidence?: boolean;
  columnVisibilityModel: Record<string, boolean>;
  setColumnVisibilityModel: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  columnOrder?: string[];
  setColumnOrder?: (order: string[]) => void;
}

type ColumnDisplay = { key: string; header: string; category?: string };

function buildDisplayFragment(
  allColumns: ColumnDefinition[],
  columnVisibilityModel: Record<string, boolean>,
  view: string,
  tab: string,
  isEvidence: boolean,
  source?: string,
): Record<string, ColumnDisplay> {
  const acc: Record<string, ColumnDisplay> = {};
  Object.keys(columnVisibilityModel).forEach(key => {
    const column: ColumnDefinition | undefined = allColumns.find(col => col.name === key);
    if (column && column.header) {
      let shouldShow = true;
      if (typeof column.show === 'function') {
        shouldShow = column.show({ tab, view, isEvidence, source });
      } else if (column.show !== undefined) {
        shouldShow = column.show;
      }
      if (shouldShow) {
        const headerLabel =
          getHeaderText(column.header) ||
          COLUMN_LABEL_FALLBACK[key] ||
          (column.name ?? key).replace(/([A-Z])/g, ' $1').trim();
        acc[key] = {
          key,
          header: headerLabel,
          category: column.category,
        };
      }
    }
  });
  return acc;
}

export function SidebarColumns({
  view,
  tab,
  source,
  isEvidence = false,
  columnVisibilityModel,
  setColumnVisibilityModel,
  columnOrder,
  setColumnOrder,
}: SidebarColumnsProps) {
  const allColumns = getAllColumns({ isEvidence, tab });
  const [search, setSearch] = useState('');
  const [draggedColumnKey, setDraggedColumnKey] = useState<string | null>(null);

  const displayFragment = useMemo(
    () => buildDisplayFragment(allColumns, columnVisibilityModel, view, tab, isEvidence, source),
    [allColumns, columnVisibilityModel, view, tab, isEvidence, source],
  );

  const defaultOrderedColumns = useMemo(
    () => allColumns.map(col => displayFragment[col.name]).filter(Boolean) as ColumnDisplay[],
    [allColumns, displayFragment],
  );
  const orderedColumns = useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) return defaultOrderedColumns;
    const byKey = new Map(defaultOrderedColumns.map(col => [col.key, col] as const));
    const fromSaved = columnOrder.map(key => byKey.get(key)).filter(Boolean) as ColumnDisplay[];
    const inSaved = new Set(fromSaved.map(col => col.key));
    const missing = defaultOrderedColumns.filter(col => !inSaved.has(col.key));
    return [...fromSaved, ...missing];
  }, [defaultOrderedColumns, columnOrder]);

  const filteredColumns = useMemo(() => {
    if (!search.trim()) return orderedColumns;
    const q = search.trim().toLowerCase();
    return orderedColumns.filter(
      c => c.key.toLowerCase().includes(q) || (c.header && String(c.header).toLowerCase().includes(q)),
    );
  }, [orderedColumns, search]);

  const handleToggle = (key: string) => {
    setColumnVisibilityModel(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const reorderColumns = (fromKey: string, toKey: string) => {
    if (!setColumnOrder || fromKey === toKey) return;
    const currentOrder = orderedColumns.map(col => col.key);
    const fromIndex = currentOrder.indexOf(fromKey);
    const toIndex = currentOrder.indexOf(toKey);
    if (fromIndex < 0 || toIndex < 0) return;
    const nextOrder = [...currentOrder];
    const [moved] = nextOrder.splice(fromIndex, 1);
    nextOrder.splice(toIndex, 0, moved);
    setColumnOrder(nextOrder);
  };

  const allKeys = useMemo(() => orderedColumns.map(c => c.key), [orderedColumns]);
  const allGlobalSelected = allKeys.length > 0 && allKeys.every(k => columnVisibilityModel[k]);

  const handleToggleAll = () => {
    setColumnVisibilityModel(prev => {
      const next = { ...prev };
      allKeys.forEach(k => {
        next[k] = !allGlobalSelected;
      });
      return next;
    });
  };

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 overflow-hidden">
      <div className="flex-shrink-0 px-3 py-2 space-y-2">
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
          </span>
          <input
            type="text"
            placeholder="Filter columns..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>
        <div className="flex items-center justify-between border-b border-gray-100 pb-2">
          <span className="text-xs text-gray-500">
            {allKeys.filter(k => columnVisibilityModel[k]).length} / {allKeys.length} selected
          </span>

          <SharedButton type="button" variant="secondaryText" size="xs" onClick={handleToggleAll}>
            {allGlobalSelected ? 'Deselect all' : 'Select all'}
          </SharedButton>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0">
        {filteredColumns.length === 0 ? (
          <p className="px-3 py-2 text-sm text-gray-500">No columns match.</p>
        ) : (
          <ul className="space-y-0.5 pt-1 px-1">
            {filteredColumns.map(col => {
              const isVisible = !!columnVisibilityModel[col.key];
              return (
                <li key={col.key}>
                  <button
                    type="button"
                    onClick={() => handleToggle(col.key)}
                    data-demo-tour="sidebar-column-toggle"
                    onDragOver={e => {
                      if (!setColumnOrder) return;
                      e.preventDefault();
                    }}
                    onDrop={e => {
                      if (!setColumnOrder || !draggedColumnKey) return;
                      e.preventDefault();
                      reorderColumns(draggedColumnKey, col.key);
                      setDraggedColumnKey(null);
                    }}
                    className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left transition-colors ${
                      isVisible ? 'bg-[#FDF2F8] hover:bg-[#FCE7F3]' : 'hover:bg-gray-100'
                    }`}
                  >
                    <span
                      draggable={!!setColumnOrder}
                      onMouseDown={e => e.stopPropagation()}
                      onClick={e => e.stopPropagation()}
                      onDragStart={e => {
                        if (!setColumnOrder) return;
                        e.stopPropagation();
                        setDraggedColumnKey(col.key);
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', col.key);
                      }}
                      onDragEnd={() => setDraggedColumnKey(null)}
                      title={setColumnOrder ? 'Drag to reorder' : undefined}
                      data-demo-tour="sidebar-column-drag"
                      className={clsx(
                        'inline-flex h-5 w-5 items-center justify-center rounded text-[#A3A9BC]',
                        setColumnOrder ? 'cursor-grab active:cursor-grabbing' : 'cursor-default',
                      )}
                    >
                      <Bars3Icon className="h-4 w-4" />
                    </span>
                    <span
                      className={`text-sm truncate flex-1 ${isVisible ? 'text-gray-800' : 'text-gray-500'}`}
                    >
                      {col.header}
                    </span>
                    {col.category && <span className="shrink-0 text-xs text-gray-400">{col.category}</span>}
                    {isVisible ? (
                      <EyeIcon className="h-4 w-4 text-[#64748B] flex-shrink-0" />
                    ) : (
                      <EyeSlashIcon className="h-4 w-4 text-[#CBD5E1] flex-shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
